import React, { HTMLAttributes } from 'react';
import { IconSize } from '../types';
import * as HeroIcons from 'heroicons-react';
import * as GikIcons from './GikIcons';
import * as BrandIcons from './BrandIcons';
import tailwindConfig from '@/../tailwind.config';
import bemBlock, { useBemCN } from '@gik/core/utils/bemBlock';

const heroIconsSet = 'hero-icons';
const gikIconsSet = 'gik-icons';
const brandIconsSet = 'brand-icons';

export const svgSets = {
  [heroIconsSet]: HeroIcons,
  [gikIconsSet]: GikIcons,
  [brandIconsSet]: BrandIcons,
};

type HeroIconsSet = typeof heroIconsSet;
type GikIconsSet = typeof gikIconsSet;
type BrandIconsSet = typeof brandIconsSet;
export type SvgSet = HeroIconsSet | GikIconsSet | BrandIconsSet;

export interface IBaseIconSetType {
  name: SvgIconName;
  set?: SvgSet;
}

export type HeroIconName = keyof typeof HeroIcons;
interface HeroSvgIconType extends IBaseIconSetType {
  name: HeroIconName;
  set?: HeroIconsSet;
}

export type GikIconName = keyof Omit<typeof GikIcons, 'GikIconDefaultSize' | 'GikIconProps'>;
interface GikSvgIconType extends IBaseIconSetType {
  name: GikIconName;
  set?: GikIconsSet;
}

export type BrandIconName = keyof Omit<typeof BrandIcons, 'BrandIconDefaultSize' | 'BrandIconProps'>;
interface BrandSvgIconType extends IBaseIconSetType {
  name: BrandIconName;
  set?: BrandIconsSet;
}

/**
 * Use this type in other component props when you need your component to be able to specify an object with icon set and icon name
 */
export type IconSetType = HeroSvgIconType | GikSvgIconType | BrandSvgIconType;

export type SvgIconName = HeroIconName | GikIconName | BrandIconName;

interface BaseSvgIconProps extends HTMLAttributes<HTMLElement>, IBaseIconSetType {
  size?: IconSize;
  strikeOut?: boolean;
}

type HeroSvgIconProps = BaseSvgIconProps & HeroSvgIconType;
type GikSvgIconProps = BaseSvgIconProps & GikSvgIconType;
type BrandSvgIconProps = BaseSvgIconProps & BrandSvgIconType;

export type SvgIconProps = HeroSvgIconProps | GikSvgIconProps | BrandSvgIconProps;

const blockName = `svg-icon`;

export function SvgIcon(props: HeroSvgIconProps): React.ReactElement;
export function SvgIcon(props: GikSvgIconProps): React.ReactElement;
export function SvgIcon(props: BrandSvgIconProps): React.ReactElement;

export default function SvgIcon({
  name,
  size = 'base',
  className,
  strikeOut,
  set = heroIconsSet,
  ...otherProps
}: SvgIconProps): React.ReactElement {
  const bem = useBemCN(blockName);

  const notFoundStr = '⛔';

  const selectedSet = svgSets[set];
  const Value = selectedSet[name];

  if (!Value) {
    console.error(`Could not find SvgIcon ${name} in set ${set}.`);
    return (
      <i {...bem()} title={`SvgIcon has no mapping: ${name}`}>
        {notFoundStr}
      </i>
    );
  }

  const svg = (
    <Value
      {...bem(
        null,
        [
          {
            [set]: true,
          },
          {
            [name as string]: true,
          },
          { strikeOut },
        ],
        className
      )}
      size={tailwindConfig.theme.iconSize[size]}
      {...otherProps}
    />
  );

  if (strikeOut) {
    return (
      <div {...bem('wrapper')}>
        {svg}
        <div {...bem('strike-line')}></div>
      </div>
    );
  }

  return svg;
}
